/**
 * Warn before submit when a home/residency address looks filled in but the map field has no coordinates
 * (geocode miss or user never placed a pin). Parents can still confirm to allow edge cases.
 *
 * Only applies to the Residency Determination (or similar) form — not other forms that may use map fields.
 */

function isResidencyFormLabel(name) {
  if (!name || typeof name !== 'string') return false;
  return name.toLowerCase().includes('residency');
}

/** Instance list from combineFormInstances: { id, instance_name, master_form_name } */
export function getResidencyFormInstanceIds(combinedFormDefinition) {
  const ids = new Set();
  const list = combinedFormDefinition?.formInstances;
  if (!Array.isArray(list)) return ids;
  list.forEach((inst) => {
    const label = `${inst.instance_name || ''} ${inst.master_form_name || ''}`;
    if (isResidencyFormLabel(label)) {
      ids.add(inst.id);
    }
  });
  return ids;
}

export function isResidencyFormInstanceMeta(meta) {
  if (!meta) return false;
  const label = `${meta.instance_name || ''} ${meta.master_form_name || ''}`;
  return isResidencyFormLabel(label);
}

function hasValidMapCoordinates(val) {
  if (val == null || val === '') return false;
  let v = val;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return false;
    }
  }
  if (typeof v !== 'object') return false;
  const lat = Number(v.lat);
  const lng = Number(v.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

/**
 * Same address assembly as MapField (prefixed keys for combined intake forms, or direct keys for modals).
 */
export function buildMapAddressFromFormValues(formValues, formInstanceId, { useInstancePrefix = true } = {}) {
  if (!formValues) return '';

  let prefixedStreet;
  let prefixedCity;
  let prefixedState;
  let prefixedZip;
  if (useInstancePrefix && formInstanceId) {
    prefixedStreet = formValues[`${formInstanceId}_home_street_address`];
    prefixedCity = formValues[`${formInstanceId}_home_city`];
    prefixedState = formValues[`${formInstanceId}_home_state`];
    prefixedZip = formValues[`${formInstanceId}_home_zip_code`];
  }

  const directStreet = formValues.home_street_address || formValues.street_address || '';
  const directCity = formValues.home_city || formValues.city || '';
  const directState = formValues.home_state || formValues.state || '';
  const directZip = formValues.home_zip_code || formValues.zip_code || formValues.zip || '';

  const street = (prefixedStreet != null && prefixedStreet !== '') ? prefixedStreet : directStreet;
  const city = (prefixedCity != null && prefixedCity !== '') ? prefixedCity : directCity;
  const state = (prefixedState != null && prefixedState !== '') ? prefixedState : directState;
  const zip = (prefixedZip != null && prefixedZip !== '') ? prefixedZip : directZip;

  const parts = [street, city, state, zip].filter((p) => p && String(p).trim());
  return parts.join(', ');
}

function isAddressSubstantialEnoughToWarn(addressString) {
  if (!addressString || typeof addressString !== 'string') return false;
  const trimmed = addressString.trim();
  if (trimmed.length < 8) return false;
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  return parts.length >= 2;
}

function collectMapFieldsFromCombinedSections(sections) {
  const out = [];
  if (!Array.isArray(sections)) return out;
  for (const section of sections) {
    for (const field of section.fields || []) {
      if (field.type === 'map' && field.name) {
        out.push({
          instanceId: field.form_instance_id,
          key: field.name
        });
      }
    }
  }
  return out;
}

function collectMapFieldsFromStandaloneSections(sections) {
  const out = [];
  if (!Array.isArray(sections)) return out;
  for (const section of sections) {
    for (const field of section.fields || []) {
      if (field.type === 'map' && field.name) {
        out.push({ key: field.name });
      }
    }
  }
  return out;
}

export function shouldConfirmUngeocodedMapAddress(combinedFormDefinition, formValues) {
  if (!combinedFormDefinition?.sections || !formValues) return false;

  const residencyIds = getResidencyFormInstanceIds(combinedFormDefinition);
  if (residencyIds.size === 0) return false;

  const mapFields = collectMapFieldsFromCombinedSections(combinedFormDefinition.sections);
  for (const { instanceId, key } of mapFields) {
    if (!residencyIds.has(instanceId)) continue;
    if (hasValidMapCoordinates(formValues[key])) continue;
    const addr = buildMapAddressFromFormValues(formValues, instanceId, { useInstancePrefix: true });
    if (isAddressSubstantialEnoughToWarn(addr)) {
      return true;
    }
  }
  return false;
}

/**
 * Modal / single-form definitions (field names not prefixed with instance id).
 * @param {object} [formInstanceMeta] - { instance_name, master_form_name } — required to scope to residency form
 */
export function shouldConfirmUngeocodedMapAddressStandalone(formDefinition, formValues, formInstanceMeta) {
  if (!isResidencyFormInstanceMeta(formInstanceMeta)) return false;
  if (!formDefinition?.sections || !formValues) return false;
  const mapFields = collectMapFieldsFromStandaloneSections(formDefinition.sections);
  for (const { key } of mapFields) {
    if (hasValidMapCoordinates(formValues[key])) continue;
    const addr = buildMapAddressFromFormValues(formValues, null, { useInstancePrefix: false });
    if (isAddressSubstantialEnoughToWarn(addr)) {
      return true;
    }
  }
  return false;
}

const CONFIRM_MESSAGE =
  'Address not found. Are you sure this is the correct address?\n\n' +
  'We could not verify a map location for this home address (or the map pin is missing). ' +
  'The student may not appear correctly on the district map.\n\n' +
  'Click OK to submit anyway, or Cancel to go back and use "Find on Map" or place the pin on the map.';

export function confirmUngeocodedMapAddressIfNeeded(combinedFormDefinition, formValues) {
  if (!shouldConfirmUngeocodedMapAddress(combinedFormDefinition, formValues)) {
    return true;
  }
  return window.confirm(CONFIRM_MESSAGE);
}

export function confirmUngeocodedMapAddressStandaloneIfNeeded(formDefinition, formValues, formInstanceMeta) {
  if (!shouldConfirmUngeocodedMapAddressStandalone(formDefinition, formValues, formInstanceMeta)) {
    return true;
  }
  return window.confirm(CONFIRM_MESSAGE);
}
